import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminModalTailwind from "./AdminModalTailwind";
import GenericSelectTailwind from "./GenericSelectTailwind";
import Toast from "./Toast";
import { groupsService, categoriesService, ageGroupsService } from '../../services';
import cacheService from '../../utils/cacheService';
import { getAgeGroupColors, sortAgeGroups } from "../../utils/ageGroupColors";

const GroupsTailwind = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedParallel, setSelectedParallel] = useState("all");
  const [selectedParticipants, setSelectedParticipants] = useState("all");
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States für Startliste
  const [showStartListModal, setShowStartListModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [isStartListEditMode, setIsStartListEditMode] = useState(false);
  const [isSavingStartList, setIsSavingStartList] = useState(false);
  const [participantOrder, setParticipantOrder] = useState([]);
  const [expandedNations, setExpandedNations] = useState(new Set()); // Für Team-Startliste: ausklappbare Nationen
  const [teamOrder, setTeamOrder] = useState([]); // Für Team-Startreihenfolge
  const [partnerTeams, setPartnerTeams] = useState([]); // Für Partner-Teams
  const [partnerTeamOrder, setPartnerTeamOrder] = useState([]); // Für Partner-Team-Startreihenfolge

  // Toast State
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  const addToast = (type, message) => setToast({ show: true, message, type });
  const hideToast = () => setToast(prev => ({ ...prev, show: false }));

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  // Lese Kategorie aus URL-Parameter und setze Filter
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prüfe Cache für Kategorien und Altersgruppen
      const cachedCategories = cacheService.get('categories');
      const cachedAgeGroups = cacheService.get('ageGroups');

      // Lade Gruppen (immer), Kategorien und Altersgruppen nur wenn nicht gecacht
      const loadPromises = [groupsService.getAllGroupsWithFallback()];
      let categoriesIndex = -1;
      let ageGroupsIndex = -1;

      if (!cachedCategories) {
        categoriesIndex = loadPromises.length;
        loadPromises.push(categoriesService.getAllCategories());
      }
      if (!cachedAgeGroups) {
        ageGroupsIndex = loadPromises.length;
        loadPromises.push(ageGroupsService.getAllAgeGroups());
      }

      const responses = await Promise.all(loadPromises);
      const groupsResponse = responses[0];

      // Verwende Cache oder Response
      let categoriesResponse = cachedCategories
        ? { success: true, data: cachedCategories }
        : (categoriesIndex >= 0 ? responses[categoriesIndex] : null);
      let ageGroupsResponse = cachedAgeGroups
        ? { success: true, data: cachedAgeGroups }
        : (ageGroupsIndex >= 0 ? responses[ageGroupsIndex] : null);

      let allGroups = [];

      if (groupsResponse.success) {
        // Stelle sicher, dass data ein Array ist (kann auch ein leeres Array sein)
        const groupsData = Array.isArray(groupsResponse.data) ? groupsResponse.data : [];

        // Falls die Backend-API die Teilnehmeranzahl nicht dynamisch berechnet,
        // berechnen wir sie hier im Frontend
        const groupsWithCount = groupsData.map(group => {
          // Wenn currentParticipants bereits korrekt ist, verwende es
          if (group.currentParticipants !== undefined && group.currentParticipants !== null) {
            return group;
          }

          // Ansonsten setze es auf 0 (wird später dynamisch berechnet)
          return {
            ...group,
            currentParticipants: 0
          };
        });

        allGroups = groupsWithCount;
      } else {
        setError(groupsResponse.error || 'Fehler beim Laden der Gruppen');
      }

      // Team-Gruppe wird jetzt über getAllGroups geladen (ist eine echte DB-Gruppe)
      // Keine separate Ladung mehr nötig

      setGroups(allGroups);

      if (categoriesResponse && categoriesResponse.success) {
        setCategories(categoriesResponse.data);
        if (!cachedCategories) {
          cacheService.set('categories', categoriesResponse.data);
        }
      } else if (categoriesResponse) {
        setError(categoriesResponse.error);
      }

      if (ageGroupsResponse && ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
        if (!cachedAgeGroups) {
          cacheService.set('ageGroups', ageGroupsResponse.data);
        }
      } else if (ageGroupsResponse) {
        setError(ageGroupsResponse.error);
      }
    } catch (err) {
      const errorMessage = "Fehler beim Laden der Daten";
      setError(errorMessage);
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  // Event Handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  const handleParallelChange = (parallel) => {
    setSelectedParallel(parallel);
  };

  const handleParticipantsChange = (participants) => {
    setSelectedParticipants(participants);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSelectedStatus("all");
    setSelectedAgeGroup("all");
    setSelectedGender("all");
    setSelectedLocation("all");
    setSelectedParallel("all");
    setSelectedParticipants("all");
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const active = [];
    if (selectedCategory !== "all") {
      const categoryLabel = categoryOptions.find(c => c.value === selectedCategory)?.label || selectedCategory;
      active.push({ type: "Kategorie", value: categoryLabel, onRemove: () => setSelectedCategory("all") });
    }
    if (selectedStatus !== "all") {
      const statusLabel = statusOptions.find(s => s.value === selectedStatus)?.label || selectedStatus;
      active.push({ type: "Status", value: statusLabel, onRemove: () => setSelectedStatus("all") });
    }
    if (selectedAgeGroup !== "all") {
      const ageGroupLabel = ageGroupOptions.find(a => a.value === selectedAgeGroup)?.label || selectedAgeGroup;
      active.push({ type: "Altersgruppe", value: ageGroupLabel, onRemove: () => setSelectedAgeGroup("all") });
    }
    if (selectedGender !== "all") {
      const genderLabel = genderOptions.find(g => g.value === selectedGender)?.label || selectedGender;
      active.push({ type: "Geschlecht", value: genderLabel, onRemove: () => setSelectedGender("all") });
    }
    if (selectedLocation !== "all") {
      const locationLabel = locationOptions.find(l => l.value === selectedLocation)?.label || selectedLocation;
      active.push({ type: "Fläche", value: locationLabel, onRemove: () => setSelectedLocation("all") });
    }
    if (selectedParticipants !== "all") {
      const participantsLabel = participantsOptions.find(p => p.value === selectedParticipants)?.label || selectedParticipants;
      active.push({ type: "Teilnehmeranzahl", value: participantsLabel, onRemove: () => setSelectedParticipants("all") });
    }
    if (selectedParallel !== "all") {
      const parallelLabel = parallelOptions.find(p => p.value === selectedParallel)?.label || selectedParallel;
      active.push({ type: "Parallel", value: parallelLabel, onRemove: () => setSelectedParallel("all") });
    }
    if (searchTerm.trim() !== "") {
      active.push({ type: "Suche", value: searchTerm, onRemove: () => setSearchTerm("") });
    }
    return active;
  };

  const handleToggleParallel = async (group) => {
    try {
      const newTwoParallel = !group.twoParallel;
      const result = await groupsService.updateGroup(group.id, { twoParallel: newTwoParallel });

      if (result.success) {
        // Aktualisiere die Gruppe in der Liste
        setGroups(prevGroups =>
          prevGroups.map(g =>
            g.id === group.id ? { ...g, twoParallel: newTwoParallel } : g
          )
        );
        addToast('success', `Gruppe erfolgreich aktualisiert: ${newTwoParallel ? 'Zwei parallel' : 'Alleine'}`);
      } else {
        addToast('error', result.error || 'Fehler beim Aktualisieren der Gruppe');
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Gruppe:', error);
      addToast('error', 'Fehler beim Aktualisieren der Gruppe');
    }
  };

  const handleViewStartList = async (group) => {
    setSelectedGroup(group);

    // Spezielle Behandlung für Team-Gruppe
    if (group.isTeamCompetition || group.id === 'team-competition') {
      // Lade Team-Gruppe neu für aktuelle Daten
      try {
        const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
        if (teamGroupResponse.success && teamGroupResponse.data) {
          setSelectedGroup(teamGroupResponse.data);
          // Setze keine einzelnen Teilnehmer, sondern Teams-Struktur
          setGroupParticipants([]); // Wird nicht verwendet bei Teams
        }
      } catch (error) {
        console.error('Fehler beim Laden der Team-Gruppe:', error);
      }
      setShowStartListModal(true);
      return;
    }

    try {
      // Versuche echte Teilnehmerdaten mit Startnummern aus der Backend-API zu laden
      const startOrderResponse = await groupsService.getGroupWithStartOrder(group.id);

      if (startOrderResponse.success) {
        // Prüfe ob es eine Partnerform ist
        if (startOrderResponse.data.isPartnerForm && startOrderResponse.data.partnerTeams) {
          setPartnerTeams(startOrderResponse.data.partnerTeams || []);
          setGroupParticipants([]); // Wird nicht verwendet bei Partner-Teams
          setSelectedGroup(startOrderResponse.data);
        } else {
          // Verwende die echten Daten aus der API
          setGroupParticipants(startOrderResponse.data.participants || []);
          setPartnerTeams([]);
        }
      } else {
        // Fallback: Versuche echte Teilnehmerdaten aus der Backend-API zu laden
        const participantsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/participants`);

        if (participantsResponse.ok) {
          const participantsData = await participantsResponse.json();

          if (participantsData.success) {
            // Filtere Teilnehmer basierend auf der Gruppe
            const categoryName = group.category?.name || group.category;
            const ageGroupName = group.ageGroup?.name || group.ageGroup;

            const filteredParticipants = participantsData.data.filter(p => {
              // Prüfe ob Teilnehmer in der passenden Kategorie ist
              const hasMatchingCategory = p.categories && p.categories.some(cat =>
                cat.name === categoryName || cat === categoryName
              );

              // Prüfe Altersgruppe und Geschlecht
              const hasMatchingAgeGroup = p.ageGroup?.name === ageGroupName;
              const hasMatchingGender = p.gender.toLowerCase() === group.gender.toLowerCase();

              return hasMatchingCategory && hasMatchingAgeGroup && hasMatchingGender;
            });

            setGroupParticipants(filteredParticipants);
          } else {
            console.error('API-Fehler:', participantsData.message);
            setGroupParticipants([]);
          }
        } else {
          console.error('HTTP-Fehler beim Laden der Teilnehmer');
          setGroupParticipants([]);
        }
      }
    } catch (error) {
      console.error('Netzwerkfehler beim Laden der Teilnehmer:', error);
      setGroupParticipants([]);
    }

    setShowStartListModal(true);
  };

  const handleCloseStartListModal = () => {
    setShowStartListModal(false);
    setSelectedGroup(null);
    setGroupParticipants([]);
    setIsStartListEditMode(false);
    setParticipantOrder([]);
    setExpandedNations(new Set());
  };

  const handleToggleNationExpansion = (nationId) => {
    setExpandedNations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nationId)) {
        newSet.delete(nationId);
      } else {
        newSet.add(nationId);
      }
      return newSet;
    });
  };

  // Prüfe ob die Startliste bearbeitet werden darf (nur bei Status "Ausstehend")
  const isStartListEditable = selectedGroup?.status === 'Ausstehend';

  const handleToggleStartListEditMode = () => {
    if (!isStartListEditable && !isStartListEditMode) return; // Nicht editierbar
    if (isStartListEditMode) {
      // Beim Verlassen des Edit-Modus alle Änderungen verwerfen
      setParticipantOrder([]);
      setTeamOrder([]);
      setPartnerTeamOrder([]);
    } else {
      // Beim Aktivieren des Edit-Modus die aktuelle Reihenfolge setzen
      if (selectedGroup?.isTeamCompetition && selectedGroup.teams) {
        // Für Teams: Startnummern aus Teams übernehmen oder initialisieren
        setTeamOrder(selectedGroup.teams.map((team, index) => ({
          nationId: team.nationId,
          startNumber: team.startNumber || (index + 1)
        })));
      } else if (selectedGroup?.isPartnerForm && partnerTeams.length > 0) {
        // Für Partner-Teams: Startnummern aus Partner-Teams übernehmen oder initialisieren
        setPartnerTeamOrder(partnerTeams.map((team, index) => ({
          partnerTeamId: team.id,
          startNumber: team.startNumber || (index + 1)
        })));
      } else {
        setParticipantOrder([...groupParticipants]);
      }
    }
    setIsStartListEditMode(!isStartListEditMode);
  };

  const handleSaveStartListChanges = async () => {
    setIsSavingStartList(true);
    try {
      // Für Team-Gruppen: Speichere Team-Reihenfolge
      if (selectedGroup?.isTeamCompetition) {
        const response = await groupsService.updateTeamStartOrder(teamOrder);

        if (response.success) {
          // Aktualisiere die Team-Gruppe mit neuen Startnummern
          const updatedGroup = { ...selectedGroup };
          updatedGroup.teams = updatedGroup.teams.map(team => {
            const orderItem = teamOrder.find(to => to.nationId === team.nationId);
            return {
              ...team,
              startNumber: orderItem?.startNumber || null
            };
          }).sort((a, b) => {
            const aStart = a.startNumber || 999;
            const bStart = b.startNumber || 999;
            return aStart - bStart;
          });
          setSelectedGroup(updatedGroup);
          setIsStartListEditMode(false);
          setTeamOrder([]);
          addToast('success', 'Startreihenfolge der Teams erfolgreich gespeichert');
        } else {
          addToast('error', response.error || 'Fehler beim Speichern der Startreihenfolge');
          console.error("Fehler beim Speichern:", response.error);
        }
      } else if (selectedGroup?.isPartnerForm) {
        // Für Partner-Teams: Speichere Partner-Team-Reihenfolge
        const response = await groupsService.updateStartOrder(selectedGroup.id, [], partnerTeamOrder);

        if (response.success) {
          // Aktualisiere die Partner-Teams mit neuen Startnummern
          const updatedPartnerTeams = partnerTeams.map(team => {
            const orderItem = partnerTeamOrder.find(to => to.partnerTeamId === team.id);
            return {
              ...team,
              startNumber: orderItem?.startNumber || null
            };
          }).sort((a, b) => {
            const aStart = a.startNumber || 999;
            const bStart = b.startNumber || 999;
            return aStart - bStart;
          });
          setPartnerTeams(updatedPartnerTeams);
          setIsStartListEditMode(false);
          setPartnerTeamOrder([]);

          // Lade Daten neu
          const startOrderResponse = await groupsService.getGroupWithStartOrder(selectedGroup.id);
          if (startOrderResponse.success && startOrderResponse.data.partnerTeams) {
            setPartnerTeams(startOrderResponse.data.partnerTeams || []);
            setSelectedGroup(startOrderResponse.data);
          }

          addToast('success', 'Startreihenfolge der Partner-Teams erfolgreich gespeichert');
        } else {
          addToast('error', response.error || 'Fehler beim Speichern der Startreihenfolge');
          console.error("Fehler beim Speichern:", response.error);
        }
      } else {
        // Für normale Gruppen: Speichere Teilnehmer-Reihenfolge
        const startOrder = participantOrder.map((participant, index) => ({
          participantId: participant.id,
          startNumber: index + 1
        }));

        const response = await groupsService.updateStartOrder(selectedGroup.id, startOrder);

        if (response.success) {
          setIsStartListEditMode(false);

          // Lokale Ansicht mit neuen Startnummern aktualisieren (kein Seiten-Reload)
          const startOrderResponse = await groupsService.getGroupWithStartOrder(selectedGroup.id);
          if (startOrderResponse.success) {
            setGroupParticipants(startOrderResponse.data.participants || []);
            setSelectedGroup(startOrderResponse.data);
          } else {
            setGroupParticipants(participantOrder);
          }
          setParticipantOrder([]);
          addToast('success', 'Startreihenfolge erfolgreich gespeichert');
        } else {
          addToast('error', response.error || 'Fehler beim Speichern der Startreihenfolge');
          console.error("Fehler beim Speichern:", response.error);
        }
      }
    } catch (error) {
      addToast('error', 'Fehler beim Speichern der Startreihenfolge');
      console.error("Fehler beim Speichern:", error);
    } finally {
      setIsSavingStartList(false);
    }
  };

  const handleCancelStartListEdit = () => {
    setParticipantOrder([]);
    setTeamOrder([]);
    setPartnerTeamOrder([]);
    setIsStartListEditMode(false);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const fromIndex = Number(e.dataTransfer.getData('text/plain'));
    if (fromIndex === targetIndex) return;

    const newOrder = [...participantOrder];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(targetIndex, 0, removed);
    setParticipantOrder(newOrder);
  };

  const moveParticipantUp = (index) => {
    if (index === 0) return;
    const newOrder = [...participantOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setParticipantOrder(newOrder);
  };

  const moveParticipantDown = (index) => {
    if (index === participantOrder.length - 1) return;
    const newOrder = [...participantOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setParticipantOrder(newOrder);
  };

  const movePartnerTeamUp = (index) => {
    if (index === 0) return;
    const newOrder = [...partnerTeamOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setPartnerTeamOrder(newOrder.map((item, i) => ({ ...item, startNumber: i + 1 })));
  };

  const movePartnerTeamDown = (index) => {
    if (index === partnerTeamOrder.length - 1) return;
    const newOrder = [...partnerTeamOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setPartnerTeamOrder(newOrder.map((item, i) => ({ ...item, startNumber: i + 1 })));
  };

  const moveTeamUp = (index) => {
    if (index === 0) return;
    const newOrder = [...teamOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setTeamOrder(newOrder.map((item, i) => ({ ...item, startNumber: i + 1 })));
  };

  const moveTeamDown = (index) => {
    if (index === teamOrder.length - 1) return;
    const newOrder = [...teamOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setTeamOrder(newOrder.map((item, i) => ({ ...item, startNumber: i + 1 })));
  };

  // Fisher-Yates shuffle
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const handleShuffle = () => {
    if (selectedGroup?.isTeamCompetition) {
      setTeamOrder(shuffle(teamOrder).map((item, i) => ({ ...item, startNumber: i + 1 })));
    } else if (selectedGroup?.isPartnerForm) {
      setPartnerTeamOrder(shuffle(partnerTeamOrder).map((item, i) => ({ ...item, startNumber: i + 1 })));
    } else {
      setParticipantOrder(shuffle(participantOrder));
    }
  };

  const getNationName = (participant) => {
    // Unterstütze sowohl Backend-API als auch Mock-Daten-Struktur
    let nationCode = 'DE'; // Fallback

    if (participant.nation && typeof participant.nation === 'object') {
      // Backend-API Struktur: participant.nation = { name: "Deutschland", code: "DE" }
      nationCode = participant.nation.code || 'DE';
    } else if (typeof participant.nationCode === 'string') {
      // Mock-Daten Struktur: participant.nationCode = "DE"
      nationCode = participant.nationCode;
    } else if (typeof participant.nation === 'string') {
      // Alternative Mock-Daten Struktur: participant.nation = "Deutschland"
      // Versuche den Code aus dem Namen zu extrahieren
      const nationNames = {
        "Deutschland": "DE",
        "Schweiz": "CH",
        "Österreich": "AT",
        "China": "CN",
        "Japan": "JP"
      };
      nationCode = nationNames[participant.nation] || 'DE';
    }

    const nations = {
      "DE": "Deutschland",
      "CH": "Schweiz",
      "AT": "Österreich",
      "CN": "China",
      "JP": "Japan"
    };
    return nations[nationCode] || nationCode;
  };

  const getNationCode = (participant) => {
    // Unterstütze sowohl Backend-API als auch Mock-Daten-Struktur
    if (participant.nation && typeof participant.nation === 'object') {
      // Backend-API Struktur: participant.nation = { name: "Deutschland", code: "DE" }
      return participant.nation.code || 'DE';
    } else if (typeof participant.nationCode === 'string') {
      // Mock-Daten Struktur: participant.nationCode = "DE"
      return participant.nationCode;
    } else if (typeof participant.nation === 'string') {
      // Alternative Mock-Daten Struktur: participant.nation = "Deutschland"
      const nationNames = {
        "Deutschland": "DE",
        "Schweiz": "CH",
        "Österreich": "AT",
        "China": "CN",
        "Japan": "JP"
      };
      return nationNames[participant.nation] || 'DE';
    }
    return 'DE'; // Fallback
  };

  const handleViewResults = (group) => {
    const params = new URLSearchParams();

    // Verwende die spezifischen Werte der ausgewählten Gruppe
    if (group.category?.name || group.category) {
      params.append('category', group.category?.name || group.category);
    }
    if (group.ageGroup?.name || group.ageGroup) {
      params.append('ageGroup', group.ageGroup?.name || group.ageGroup);
    }

    // Konvertiere Geschlechtswerte für Results.js
    if (group.gender) {
      params.append('gender', group.gender);
    }

    if (group.venue) {
      params.append('location', group.venue);
    }

    navigate(`/admin/results?${params.toString()}`);
  };

  const categoryOptions = [
    { value: "all", label: t("admin.groups.allCategories", "Alle Kategorien") },
    ...categories.map((category) => ({
      value: category.name,
      label: category.name
    }))
  ];

  const statusOptions = [
    { value: "all", label: t("admin.groups.allStatus", "Alle Status") },
    { value: "Ausstehend", label: t("admin.groups.pending", "Ausstehend") },
    { value: "Aktiv", label: t("admin.groups.active", "Aktiv") },
    { value: "Vorbereitung", label: t("admin.groups.preparation", "Vorbereitung") },
    { value: "Abgeschlossen", label: t("admin.groups.completed", "Abgeschlossen") }
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.groups.allAgeGroups", "Alle Altersgruppen") },
    ...ageGroups.map((ageGroup) => ({
      value: ageGroup.name,
      label: ageGroup.name
    }))
  ];

  const genderOptions = [
    { value: "all", label: t("admin.groups.allGenders", "Alle Geschlechter") },
    { value: "männlich", label: t("admin.groups.male", "Männlich") },
    { value: "weiblich", label: t("admin.groups.female", "Weiblich") },
    { value: "gemischt", label: t("admin.groups.mixed", "Gemischt") },
  ];

  const locationOptions = [
    { value: "all", label: t("admin.groups.allLocations", "Alle Flächen") },
    { value: "Fläche A", label: "Fläche A" },
    { value: "Fläche B", label: "Fläche B" },
  ];

  const participantsOptions = [
    { value: "all", label: t("admin.groups.allParticipants", "Alle Teilnehmeranzahlen") },
    { value: "0", label: "0 Teilnehmer" },
    { value: "1-3", label: "1-3 Teilnehmer" },
    { value: "4-10", label: "4-10 Teilnehmer" },
    { value: "10+", label: "10+ Teilnehmer" }
  ];

  const parallelOptions = [
    { value: "all", label: t("admin.groups.allParallel", "Alle") },
    { value: "parallel", label: t("admin.groups.parallel", "Parallel") },
    { value: "alone", label: t("admin.groups.alone", "Alleine") }
  ];

  const getStatusLabel = (status) => {
    switch (status) {
      case "Ausstehend":
        return t("admin.groups.pending", "Ausstehend");
      case "Aktiv":
        return t("admin.groups.active", "Aktiv");
      case "Abgeschlossen":
        return t("admin.groups.completed", "Abgeschlossen");
      case "Vorbereitung":
        return t("admin.groups.preparation", "Vorbereitung");
      default:
        return status;
    }
  };

  const getCategoryName = (item) => {
    return item.category?.name || item.category || 'Unbekannt';
  };

  const getAgeGroupName = (item) => {
    return item.ageGroup?.name || item.ageGroup || 'Unbekannt';
  };

  // Funktion für kompakte Gruppennamen
  const getCompactGroupName = (group) => {
    const categoryName = getCategoryName(group);
    const ageGroupName = getAgeGroupName(group);
    const gender = group.gender;

    // Kategorie-Abkürzung (erste Buchstaben)
    const getCategoryAbbreviation = (category) => {
      const words = category.split(' ');
      if (words.length === 1) {
        return category.substring(0, 3).toUpperCase();
      }
      return words.map(word => word.charAt(0)).join('').toUpperCase();
    };

    // Altersgruppen-Abkürzungen
    const getAgeGroupAbbreviation = (ageGroup) => {
      const abbreviations = {
        'Kinder': 'K',
        'Jugend': 'J',
        'Erwachsene': 'E',
        'Senioren': 'S',
        'Senioren Plus': 'SP',
        'Masters': 'M',
        'Veterans': 'V'
      };
      return abbreviations[ageGroup] || ageGroup.substring(0, 2).toUpperCase();
    };

    // Geschlecht-Abkürzung
    const getGenderAbbreviation = (gender) => {
      if (gender === 'gemischt') return 'G';
      return gender === 'männlich' ? 'M' : 'W';
    };

    const categoryAbbr = getCategoryAbbreviation(categoryName);
    const ageGroupAbbr = getAgeGroupAbbreviation(ageGroupName);
    const genderAbbr = getGenderAbbreviation(gender);

    return `${categoryAbbr}-${ageGroupAbbr}-${genderAbbr}`;
  };

  const getParticipantCountForGroup = (group) => {
    // Verwende die Teilnehmeranzahl aus der API (group.currentParticipants)
    return group.currentParticipants || 0;
  };

  const filteredGroups = groups.filter((item) => {
    const isTeamGroup = item.name === 'Gruppenwettbewerb' || item.isTeamCompetition || item.id === 'team-competition';

    // Team-Gruppe wird wie normale Gruppen behandelt, aber mit spezieller Filterlogik
    const categoryName = item.category?.name || item.category || '';
    const ageGroupName = item.ageGroup?.name || item.ageGroup || '';
    const description = item.description || '';
    const compactName = getCompactGroupName(item);

    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      compactName.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesCategory, matchesAgeGroup, matchesGender;

    if (isTeamGroup) {
      // Spezielle Filterlogik für Team-Gruppe
      matchesCategory = selectedCategory === "all" || selectedCategory === "Gruppenperformance";
      matchesAgeGroup = selectedAgeGroup === "all" || ageGroupName === selectedAgeGroup || ageGroupName === 'Alle';
      matchesGender = selectedGender === "all" || item.gender === selectedGender || (item.gender === 'gemischt' && selectedGender === 'gemischt');
    } else {
      // Normale Filterlogik
      matchesCategory = selectedCategory === "all" || categoryName === selectedCategory;
      matchesAgeGroup = selectedAgeGroup === "all" || ageGroupName === selectedAgeGroup;
      matchesGender = selectedGender === "all" || item.gender === selectedGender;
    }

    const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;
    const matchesLocation = selectedLocation === "all" || (item.venue === selectedLocation);

    // Teilnehmeranzahl-Filter
    let matchesParticipants = true;
    if (selectedParticipants !== "all") {
      const participantCount = item.currentParticipants || 0;
      switch (selectedParticipants) {
        case "0":
          matchesParticipants = participantCount === 0;
          break;
        case "1-3":
          matchesParticipants = participantCount >= 1 && participantCount <= 3;
          break;
        case "4-10":
          matchesParticipants = participantCount >= 4 && participantCount <= 10;
          break;
        case "10+":
          matchesParticipants = participantCount > 10;
          break;
        default:
          matchesParticipants = true;
      }
    }

    // Parallel-Filter
    const matchesParallel = selectedParallel === "all" ||
      (selectedParallel === "parallel" && item.twoParallel === true) ||
      (selectedParallel === "alone" && (item.twoParallel === false || item.twoParallel === null || item.twoParallel === undefined));

    return matchesSearch && matchesCategory && matchesStatus && matchesAgeGroup && matchesGender && matchesLocation && matchesParticipants && matchesParallel;
  });

  // KPI Berechnungen
  const calculateKPIs = () => {
    if (filteredGroups.length === 0) {
      return {
        totalCount: 0,
        averageParticipants: 0,
        completedCount: 0
      };
    }

    // Anzahl Gruppen
    const totalCount = filteredGroups.length;

    // Durchschnittliche Anzahl Athleten pro Gruppe
    const totalParticipants = filteredGroups.reduce((sum, group) => {
      const count = group.currentParticipants !== undefined ? group.currentParticipants : getParticipantCountForGroup(group);
      return sum + (count || 0);
    }, 0);
    const averageParticipants = totalCount > 0 ? (totalParticipants / totalCount) : 0;

    // Anzahl abgeschlossener Gruppen
    const completedCount = filteredGroups.filter(group => group.status === 'Abgeschlossen').length;

    return {
      totalCount,
      averageParticipants: Math.round(averageParticipants * 10) / 10, // Auf 1 Dezimalstelle gerundet
      completedCount
    };
  };

  const kpis = calculateKPIs();

  // Pagination Logic
  const totalItems = filteredGroups.length;
  const itemsPerPageNum = itemsPerPage === "all" ? totalItems : (typeof itemsPerPage === "string" ? parseInt(itemsPerPage) : itemsPerPage);
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPageNum);
  const startIndex = itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPageNum;
  const endIndex = itemsPerPage === "all" ? totalItems : startIndex + itemsPerPageNum;
  const paginatedGroups = itemsPerPage === "all"
    ? filteredGroups
    : filteredGroups.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedStatus, selectedAgeGroup, selectedGender, selectedLocation, selectedParallel, selectedParticipants, itemsPerPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === "all" ? "all" : parseInt(value));
    setCurrentPage(1);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-6 animate-pulse">
            <div className="flex items-center gap-3 mb-1.5">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-56"></div>
              <div className="h-6 w-14 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-72"></div>
          </div>

          {/* Filter Section Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            {/* Search Bar Skeleton (top) */}
            <div className="mb-5">
              <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>

            {/* Row 1: 4 Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-14 mb-1.5"></div>
                  <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                </div>
              ))}
            </div>

            {/* Row 2: 3 Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i}>
                  <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-1.5"></div>
                  <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                </div>
              ))}
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 animate-pulse">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                    <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(9)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(9)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900 transition-colors">
        <main className="pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">{error}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Bitte versuche es erneut.</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white text-sm font-medium rounded-xl shadow-sm transition-colors"
                >
                  <i className="fas fa-redo text-xs"></i>
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                    {t("admin.groups.title", "Gruppenverwaltung")}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <i className="fas fa-layer-group text-[10px]"></i>
                    {filteredGroups.length}
                  </span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t("admin.groups.subtitle", "Verwalte alle Gruppen und deren Zuordnung.")}
                </p>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div id="filter-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            {/* Search Bar (top) */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("admin.groups.search", "Suche nach Gruppe oder Kategorie...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-10 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700/50 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>
            </div>

            {/* Row 1: 4 Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Kategorie
                </label>
                <GenericSelectTailwind
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  options={categoryOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="Kategorie auswählen..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Status
                </label>
                <GenericSelectTailwind
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  options={statusOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                    icon:
                      opt.value === "Ausstehend" ? <i className="fas fa-clock text-amber-500" /> :
                        opt.value === "Aktiv" ? <i className="fas fa-play text-emerald-500" /> :
                          opt.value === "Vorbereitung" ? <i className="fas fa-hourglass-half text-orange-500" /> :
                            opt.value === "Abgeschlossen" ? <i className="fas fa-check text-slate-500" /> :
                              null
                  }))}
                  leftIcon={<i className="fas fa-traffic-light text-slate-400 dark:text-slate-500" />}
                  placeholder="Status auswählen..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Altersgruppe
                </label>
                <GenericSelectTailwind
                  value={selectedAgeGroup}
                  onChange={handleAgeGroupChange}
                  options={ageGroupOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="Altersgruppe auswählen..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Fläche
                </label>
                <GenericSelectTailwind
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  options={locationOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="Fläche auswählen..."
                />
              </div>
            </div>

            {/* Row 2: 2 Filters + Teilnehmeranzahl */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Geschlecht
                </label>
                <GenericSelectTailwind
                  value={selectedGender}
                  onChange={handleGenderChange}
                  options={genderOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                    icon:
                      opt.value === "männlich" ? <i className="fas fa-mars text-blue-600" /> :
                        opt.value === "weiblich" ? <i className="fas fa-venus text-pink-600" /> :
                          opt.value === "gemischt" ? <i className="fas fa-venus-mars text-purple-600" /> :
                            null
                  }))}
                  leftIcon={<i className="fas fa-venus-mars text-slate-400 dark:text-slate-500" />}
                  placeholder="Geschlecht auswählen..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Parallel
                </label>
                <GenericSelectTailwind
                  value={selectedParallel}
                  onChange={handleParallelChange}
                  options={parallelOptions.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                    icon:
                      opt.value === "parallel" ? <i className="fas fa-users text-slate-600" /> :
                        opt.value === "alone" ? <i className="fas fa-user text-slate-600" /> :
                          null
                  }))}
                  leftIcon={<i className="fas fa-user-friends text-slate-400 dark:text-slate-500" />}
                  placeholder="Parallel auswählen..."
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                  Teilnehmeranzahl
                </label>
                <GenericSelectTailwind
                  value={selectedParticipants}
                  onChange={handleParticipantsChange}
                  options={participantsOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  placeholder="Teilnehmeranzahl auswählen..."
                />
              </div>
            </div>

            {/* Active Filters Section */}
            {getActiveFilters().length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-700/60 pt-4 mt-1">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    Aktive Filter
                  </span>
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-medium transition-colors border bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:text-primary dark:hover:text-primary-light"
                  >
                    <i className="fas fa-undo text-[10px]"></i>
                    Zurücksetzen
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {getActiveFilters().map((filter, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/8 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20"
                    >
                      {filter.type}: {filter.value}
                      <button onClick={filter.onRemove} className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity">
                        <i className="fas fa-times text-[9px]"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Anzahl Gruppen */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Anzahl Gruppen
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {kpis.totalCount}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <i className="fas fa-layer-group text-lg"></i>
              </div>
            </div>

            {/* Durchschnittliche Anzahl Athleten pro Gruppe */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Ø Athleten pro Gruppe
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {kpis.averageParticipants}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <i className="fas fa-users text-lg"></i>
              </div>
            </div>

            {/* Anzahl abgeschlossener Gruppen */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Abgeschlossene Gruppen
                </p>
                <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                  {kpis.completedCount}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-400">
                <i className="fas fa-check-circle text-lg"></i>
              </div>
            </div>
          </div>

          {/* Table Section */}
          {filteredGroups.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                  <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                        {t("admin.groups.name", "Gruppe")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-40">
                        {t("admin.groups.category", "Kategorie")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                        {t("admin.groups.ageGroup", "Altersgruppe")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32">
                        {t("admin.groups.gender", "Geschlecht")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24">
                        {t("admin.groups.location", "Fläche")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-20">
                        {t("admin.groups.participants", "Teiln.")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24">
                        {t("admin.groups.parallel", "Parallel")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24">
                        {t("admin.groups.status", "Status")}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-20">
                        {t("admin.groups.actions", "Aktionen")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {paginatedGroups.map((item) => {
                      const isTeamGroup = item.isTeamCompetition || item.id === 'team-competition';

                      return (
                        <tr key={item.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors ${isTeamGroup ? 'bg-blue-50/30 dark:bg-blue-900/20' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono font-medium text-slate-900 dark:text-slate-100" title={item.name}>
                              {isTeamGroup ? item.name : getCompactGroupName(item)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-600/30 dark:border-slate-400/30"
                              title={getCategoryName(item)}
                            >
                              {getCategoryName(item)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const ageGroupName = getAgeGroupName(item);
                              const colors = getAgeGroupColors(ageGroupName);
                              const darkBgMap = {
                                'bg-emerald-100': 'dark:bg-emerald-900/30',
                                'bg-blue-100': 'dark:bg-blue-900/30',
                                'bg-violet-100': 'dark:bg-violet-900/30',
                                'bg-amber-100': 'dark:bg-amber-900/30',
                                'bg-red-100': 'dark:bg-red-900/30',
                                'bg-gray-100': 'dark:bg-gray-700'
                              };
                              const darkTextMap = {
                                'text-emerald-700': 'dark:text-emerald-300',
                                'text-blue-700': 'dark:text-blue-300',
                                'text-violet-700': 'dark:text-violet-300',
                                'text-amber-700': 'dark:text-amber-300',
                                'text-red-700': 'dark:text-red-300',
                                'text-gray-800': 'dark:text-gray-300'
                              };
                              const borderMap = {
                                'bg-emerald-100': 'border-emerald-700/30 dark:border-emerald-300/30',
                                'bg-blue-100': 'border-blue-700/30 dark:border-blue-300/30',
                                'bg-violet-100': 'border-violet-700/30 dark:border-violet-300/30',
                                'bg-amber-100': 'border-amber-700/30 dark:border-amber-300/30',
                                'bg-red-100': 'border-red-700/30 dark:border-red-300/30',
                                'bg-gray-100': 'border-gray-600/30 dark:border-gray-400/30'
                              };
                              return (
                                <span
                                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${colors.bgLight} ${darkBgMap[colors.bgLight] || 'dark:bg-slate-700'} ${colors.text} ${darkTextMap[colors.text] || 'dark:text-slate-300'} ${borderMap[colors.bgLight] || 'border-slate-600/30 dark:border-slate-400/30'}`}
                                  title={ageGroupName}
                                >
                                  {ageGroupName}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const isPartnerForm = item.category?.type === 'Partnerform';
                              const isMixedGroup = item.gender === 'gemischt' || isTeamGroup || isPartnerForm;
                              const label = isMixedGroup
                                ? 'Gemischt'
                                : item.gender === 'männlich'
                                  ? t("admin.groups.male", "Männlich")
                                  : t("admin.groups.female", "Weiblich");
                              const iconClass = isMixedGroup
                                ? 'fa-venus-mars text-purple-500'
                                : item.gender === 'männlich'
                                  ? 'fa-mars text-blue-500'
                                  : 'fa-venus text-pink-500';

                              return (
                                <div className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300">
                                  <i className={`fas ${iconClass} text-xs`} />
                                  <span>{label}</span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-slate-900 dark:text-slate-100">
                              {item.venue || <span className="text-slate-400 dark:text-slate-500">-</span>}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {(() => {
                                const isPartnerForm = item.category?.type === 'Partnerform';
                                const count = item.currentParticipants !== undefined ? item.currentParticipants : getParticipantCountForGroup(item);
                                return isPartnerForm ? `${count} Teams` : count;
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => handleToggleParallel(item)}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border-2 transition-colors focus:outline-none ${
                                item.twoParallel
                                  ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light'
                                  : 'bg-slate-200 dark:bg-slate-600 border-transparent'
                              }`}
                              title={item.twoParallel ? "Zwei parallel – klicken zum Ändern" : "Alleine – klicken zum Ändern"}
                            >
                              <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${item.twoParallel ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${item.status === 'Ausstehend' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-700/30 dark:border-yellow-300/30' :
                              item.status === 'Aktiv' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-700/30 dark:border-emerald-300/30' :
                                item.status === 'Vorbereitung' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-700/30 dark:border-orange-300/30' :
                                  item.status === 'Abgeschlossen' ? 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30' :
                                    'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30'
                              }`} title={getStatusLabel(item.status)}>
                              <i className={`fas ${item.status === 'Aktiv' ? 'fa-play' :
                                item.status === 'Ausstehend' ? 'fa-clock' :
                                  item.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                    'fa-check'
                                } mr-1.5 text-[10px]`}></i>
                              {getStatusLabel(item.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleViewStartList(item)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                                title={t("admin.groups.startList", "Startliste")}
                              >
                                <i className="fas fa-list text-[11px]"></i>
                              </button>
                              <button
                                onClick={() => handleViewResults(item)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-amber-500 dark:hover:text-amber-400 hover:border-amber-200 dark:hover:border-amber-700/50 transition-colors"
                                title={t("admin.groups.results", "Ergebnisse")}
                              >
                                <i className="fas fa-trophy text-[11px]"></i>
                              </button>
                              {item.status === 'Abgeschlossen' && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(`/scorekeeper?groupId=${encodeURIComponent(item.id)}&adminEdit=true`);
                                  }}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary-light hover:border-primary/30 dark:hover:border-primary-light/30 transition-colors"
                                  title="Punkte bearbeiten"
                                >
                                  <i className="fas fa-edit text-[11px]"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Pagination Footer Row */}
                  <tfoot className="bg-slate-50 dark:bg-slate-700/30">
                    <tr>
                      <td colSpan={9} className="px-6 py-4">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                          {/* Items per page selector */}
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Zeige
                            </span>
                            <div className="relative" style={{ zIndex: showStartListModal ? 1 : 100, pointerEvents: showStartListModal ? 'none' : 'auto' }}>
                              <GenericSelectTailwind
                                value={itemsPerPage.toString()}
                                onChange={(value) => handleItemsPerPageChange(value === "all" ? "all" : parseInt(value))}
                                options={[
                                  { value: "10", label: "10" },
                                  { value: "20", label: "20" },
                                  { value: "50", label: "50" },
                                  { value: "100", label: "100" },
                                  { value: "all", label: "Alle" }
                                ]}
                                placeholder="10"
                                openUpward={true}
                                whiteBackground={true}
                                disabled={showStartListModal}
                              />
                            </div>
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Einträge pro Seite
                            </span>
                          </div>

                          {/* Pagination Info and Controls */}
                          <div className="flex items-center gap-4">
                            {/* Info Text */}
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              Zeige <span className="font-semibold text-slate-900 dark:text-slate-100">{startIndex + 1}</span>-<span className="font-semibold text-slate-900 dark:text-slate-100">{Math.min(endIndex, totalItems)}</span> von <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems}</span> Gruppen
                            </span>

                            {/* Scroll to Top Button */}
                            <button
                              onClick={scrollToTop}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                              title="Nach oben scrollen"
                            >
                              <i className="fas fa-arrow-up text-xs"></i>
                            </button>

                            {/* Pagination Buttons */}
                            {itemsPerPage !== "all" && totalPages > 1 && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-left text-xs"></i>
                                </button>

                                {/* Page Numbers */}
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border transition-colors ${currentPage === pageNum
                                        ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30'
                                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                        }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-right text-xs"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {filteredGroups.length === 0 && !loading && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-12 text-center">
              <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700/60 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-layer-group text-slate-400 dark:text-slate-500 text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Keine Gruppen gefunden</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                {searchTerm || selectedCategory !== "all" || selectedStatus !== "all" || selectedAgeGroup !== "all" || selectedGender !== "all" || selectedLocation !== "all" || selectedParallel !== "all" || selectedParticipants !== "all"
                  ? "Versuche, die Filter zu ändern oder den Suchbegriff anzupassen."
                  : "Gruppen werden automatisch basierend auf den vorhandenen Teilnehmern generiert."
                }
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Startliste Modal */}
      {showStartListModal && (
        <AdminModalTailwind
          isOpen={showStartListModal}
          onClose={handleCloseStartListModal}
          title={selectedGroup ? (selectedGroup.isTeamCompetition ? `Startliste - ${selectedGroup.name}` : `Startliste - ${getCompactGroupName(selectedGroup)}`) : "Startliste"}
          subtitle={selectedGroup?.isTeamCompetition
            ? `${selectedGroup.teams?.length || 0} Nationen mit ${selectedGroup.currentParticipants || 0} Teilnehmern`
            : selectedGroup ? `${groupParticipants.length} Teilnehmer in ${getCategoryName(selectedGroup)} (${getAgeGroupName(selectedGroup)}, ${selectedGroup.gender})` : ""
          }
          onSave={isStartListEditMode ? handleSaveStartListChanges : null}
          onCancel={isStartListEditMode ? handleCancelStartListEdit : null}
          saveButtonText={isStartListEditMode ? "Speichern" : null}
          cancelButtonText={isStartListEditMode ? "Abbrechen" : null}
          isSaving={isSavingStartList}
        >
          <div className="space-y-6">
            {selectedGroup && !selectedGroup.isTeamCompetition && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                    <i className="fas fa-info-circle text-[9px]"></i>
                    Gruppeninformationen
                  </span>
                </div>
                <div className="p-5 overflow-visible">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 block mb-1.5">Kategorie</span>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${selectedGroup.category?.type === 'Handform' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-700/30 dark:border-blue-300/30' :
                          selectedGroup.category?.type === 'Waffenform' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-700/30 dark:border-green-300/30' :
                            selectedGroup.category?.type === 'Offene Form' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-700/30 dark:border-purple-300/30' :
                              selectedGroup.category?.type === 'Partnerform' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-700/30 dark:border-orange-300/30' :
                                'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-600/30 dark:border-slate-400/30'
                          }`}>
                          {getCategoryName(selectedGroup)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 block mb-1.5">Altersgruppe</span>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${(() => {
                          const ageGroupName = getAgeGroupName(selectedGroup);
                          const colors = getAgeGroupColors(ageGroupName);
                          const darkModeMap = {
                            'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-300/30',
                            'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-300/30',
                            'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-300/30',
                            'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-300/30',
                            'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300 dark:border-red-300/30'
                          };
                          const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300 dark:border-slate-400/30';
                          const borderMap = {
                            'Kinder': 'border-emerald-700/30',
                            'Jugend': 'border-blue-700/30',
                            'Erwachsene': 'border-violet-700/30',
                            'Senioren': 'border-amber-700/30',
                            'Senioren Plus': 'border-red-700/30'
                          };
                          const borderClass = borderMap[ageGroupName] || 'border-slate-600/30';
                          return `${colors.bgLight} ${colors.text} ${borderClass} ${darkModeClasses}`;
                        })()
                          }`}>
                          {getAgeGroupName(selectedGroup)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 block mb-1.5">Geschlecht</span>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${selectedGroup.gender === 'männlich' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-700/30 dark:border-blue-300/30' :
                          selectedGroup.gender === 'weiblich' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-700/30 dark:border-pink-300/30' :
                            'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-700/30 dark:border-purple-300/30'
                          }`}>
                          {selectedGroup.gender === "männlich" ? t("admin.groups.male", "Männlich") :
                            selectedGroup.gender === "weiblich" ? t("admin.groups.female", "Weiblich") :
                              selectedGroup.gender === "gemischt" ? "Gemischt" : selectedGroup.gender}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 block mb-1.5">Fläche</span>
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300">{selectedGroup.venue || <span className="text-slate-400 dark:text-slate-500 font-normal">–</span>}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Partner-Team Startliste */}
            {selectedGroup?.isPartnerForm && partnerTeams.length > 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                      <i className="fas fa-handshake text-[9px]"></i>
                      Partner-Teams
                      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[10px] font-semibold text-slate-500 dark:text-slate-400">{partnerTeams.length}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {isStartListEditMode ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-[11px] font-semibold">
                          <i className="fas fa-pencil-alt text-[9px]"></i>
                          Bearbeitung aktiv
                        </span>
                      ) : isStartListEditable ? (
                        <button
                          onClick={handleToggleStartListEditMode}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/30 dark:hover:border-primary-light/30 hover:text-primary dark:hover:text-primary-light transition-colors"
                        >
                          <i className="fas fa-sort text-[10px]"></i>
                          Reihenfolge
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500" title={`Bearbeitung nur bei Status "Ausstehend" möglich`}>
                          <i className="fas fa-lock text-[9px]"></i>
                          {selectedGroup?.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Edit mode banner */}
                {isStartListEditMode && (
                  <div className="mx-4 mt-4 px-3 py-2 bg-primary/5 dark:bg-primary-light/10 border border-primary/20 dark:border-primary-light/20 rounded-xl flex items-center gap-2">
                    <i className="fas fa-info-circle text-primary dark:text-primary-light text-xs flex-shrink-0"></i>
                    <span className="text-xs text-primary dark:text-primary-light flex-1">
                      Drag & Drop oder Pfeiltasten · Reihenfolge bestimmt Startnummer
                    </span>
                    <button
                      onClick={handleShuffle}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-primary/20 dark:border-primary-light/20 bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-xs font-medium hover:bg-primary/20 dark:hover:bg-primary-light/20 transition-colors"
                      title="Reihenfolge zufällig mischen"
                    >
                      <i className="fas fa-random text-[10px]"></i>
                      Shuffle
                    </button>
                  </div>
                )}

                <div className="p-4 space-y-2">
                  {(isStartListEditMode ? partnerTeamOrder.map((to, i) => ({ team: partnerTeams.find(t => t.id === to.partnerTeamId), idx: i })).filter(x => x.team) : partnerTeams.map((team, i) => ({ team, idx: i }))).map(({ team, idx }) => {
                    const isFirst = idx === 0;
                    const isLast = idx === (isStartListEditMode ? partnerTeamOrder.length : partnerTeams.length) - 1;
                    const startNum = isStartListEditMode
                      ? (partnerTeamOrder.find(to => to.partnerTeamId === team.id)?.startNumber)
                      : team.startNumber;

                    return (
                      <div
                        key={team.id}
                        draggable={isStartListEditMode}
                        onDragStart={isStartListEditMode ? (e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', team.id.toString());
                        } : undefined}
                        onDragOver={isStartListEditMode ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                        onDrop={isStartListEditMode ? (e) => {
                          e.preventDefault();
                          const draggedId = parseInt(e.dataTransfer.getData('text/plain'));
                          if (draggedId === team.id) return;
                          const newOrder = [...partnerTeamOrder];
                          const fromIdx = newOrder.findIndex(to => to.partnerTeamId === draggedId);
                          const toIdx = newOrder.findIndex(to => to.partnerTeamId === team.id);
                          if (fromIdx !== -1 && toIdx !== -1) {
                            const [removed] = newOrder.splice(fromIdx, 1);
                            newOrder.splice(toIdx, 0, removed);
                            setPartnerTeamOrder(newOrder.map((item, i) => ({ ...item, startNumber: i + 1 })));
                          }
                        } : undefined}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                          isStartListEditMode
                            ? 'cursor-grab bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:border-primary/30 dark:hover:border-primary-light/30 hover:shadow-sm'
                            : 'bg-slate-50/50 dark:bg-slate-700/20 border-transparent'
                        }`}
                      >
                        {isStartListEditMode && (
                          <i className="fas fa-grip-vertical text-slate-300 dark:text-slate-600 flex-shrink-0"></i>
                        )}
                        <span className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          isStartListEditMode
                            ? 'bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                        }`}>
                          {startNum || (idx + 1)}
                        </span>

                        {/* Teilnehmer 1 */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {team.participant1?.nation?.code && (
                            <img
                              src={process.env.PUBLIC_URL + "/images/flags/" + team.participant1.nation.code + ".png"}
                              alt={team.participant1.nation.name}
                              className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{team.participant1?.name || '–'}</div>
                            <div className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">{team.participant1?.participantNumber || team.participant1?.id}</div>
                          </div>
                        </div>

                        <i className="fas fa-handshake text-slate-300 dark:text-slate-600 flex-shrink-0 text-xs"></i>

                        {/* Teilnehmer 2 */}
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {team.participant2?.nation?.code && (
                            <img
                              src={process.env.PUBLIC_URL + "/images/flags/" + team.participant2.nation.code + ".png"}
                              alt={team.participant2.nation.name}
                              className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{team.participant2?.name || '–'}</div>
                            <div className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">{team.participant2?.participantNumber || team.participant2?.id}</div>
                          </div>
                        </div>

                        {isStartListEditMode && (
                          <div className="flex flex-col gap-0.5 flex-shrink-0">
                            <button
                              onClick={() => movePartnerTeamUp(idx)}
                              disabled={isFirst}
                              className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-chevron-up text-[9px]"></i>
                            </button>
                            <button
                              onClick={() => movePartnerTeamDown(idx)}
                              disabled={isLast}
                              className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-chevron-down text-[9px]"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Team-Gruppe Startliste */}
            {selectedGroup?.isTeamCompetition && selectedGroup.teams ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                      <i className="fas fa-users text-[9px]"></i>
                      Teams
                      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[10px] font-semibold text-slate-500 dark:text-slate-400">{selectedGroup.teams.length} Nationen · {selectedGroup.currentParticipants || 0} Athleten</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {isStartListEditMode ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-[11px] font-semibold">
                          <i className="fas fa-pencil-alt text-[9px]"></i>
                          Bearbeitung aktiv
                        </span>
                      ) : isStartListEditable ? (
                        <button
                          onClick={handleToggleStartListEditMode}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/30 dark:hover:border-primary-light/30 hover:text-primary dark:hover:text-primary-light transition-colors"
                        >
                          <i className="fas fa-sort text-[10px]"></i>
                          Reihenfolge
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500" title={`Bearbeitung nur bei Status "Ausstehend" möglich`}>
                          <i className="fas fa-lock text-[9px]"></i>
                          {selectedGroup?.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Edit mode banner */}
                {isStartListEditMode && (
                  <div className="mx-4 mt-4 px-3 py-2 bg-primary/5 dark:bg-primary-light/10 border border-primary/20 dark:border-primary-light/20 rounded-xl flex items-center gap-2">
                    <i className="fas fa-info-circle text-primary dark:text-primary-light text-xs flex-shrink-0"></i>
                    <span className="text-xs text-primary dark:text-primary-light flex-1">
                      Drag & Drop oder Pfeiltasten · Reihenfolge bestimmt Startnummer
                    </span>
                    <button
                      onClick={handleShuffle}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-primary/20 dark:border-primary-light/20 bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-xs font-medium hover:bg-primary/20 dark:hover:bg-primary-light/20 transition-colors"
                      title="Reihenfolge zufällig mischen"
                    >
                      <i className="fas fa-random text-[10px]"></i>
                      Shuffle
                    </button>
                  </div>
                )}

                <div className={`${isStartListEditMode ? 'p-4 space-y-2' : ''} overflow-visible rounded-b-xl`}>
                  <div className={isStartListEditMode ? '' : 'divide-y divide-slate-200 dark:divide-slate-700'}>
                    {(isStartListEditMode ? teamOrder.map((to, i) => ({ team: selectedGroup.teams.find(t => t.nationId === to.nationId), idx: i })).filter(x => x.team) : selectedGroup.teams.map((team, i) => ({ team, idx: i }))).map(({ team, idx }) => {
                      const isExpanded = expandedNations.has(team.nationId);
                      const totalParticipants = team.participants?.length || 0;
                      const startNum = isStartListEditMode
                        ? (teamOrder.find(to => to.nationId === team.nationId)?.startNumber)
                        : team.startNumber;
                      const isFirst = idx === 0;
                      const isLast = idx === (isStartListEditMode ? teamOrder.length : selectedGroup.teams.length) - 1;

                      return (
                        <div
                          key={team.nationId}
                          draggable={isStartListEditMode}
                          onDragStart={isStartListEditMode ? (e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', team.nationId.toString());
                          } : undefined}
                          onDragOver={isStartListEditMode ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                          onDrop={isStartListEditMode ? (e) => {
                            e.preventDefault();
                            const draggedNationId = parseInt(e.dataTransfer.getData('text/plain'));
                            if (draggedNationId === team.nationId) return;
                            const newOrder = [...teamOrder];
                            const fromIdx = newOrder.findIndex(to => to.nationId === draggedNationId);
                            const toIdx = newOrder.findIndex(to => to.nationId === team.nationId);
                            if (fromIdx !== -1 && toIdx !== -1) {
                              const [removed] = newOrder.splice(fromIdx, 1);
                              newOrder.splice(toIdx, 0, removed);
                              setTeamOrder(newOrder.map((item, i) => ({ ...item, startNumber: i + 1 })));
                            }
                          } : undefined}
                          className={isStartListEditMode
                            ? 'cursor-grab flex items-center gap-3 px-3 py-2.5 rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:border-primary/30 dark:hover:border-primary-light/30 hover:shadow-sm transition-all'
                            : 'transition-colors'
                          }
                        >
                          {isStartListEditMode ? (
                            <>
                              <i className="fas fa-grip-vertical text-slate-300 dark:text-slate-600 flex-shrink-0"></i>
                              <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light">
                                {startNum || (idx + 1)}
                              </span>
                              <img
                                src={process.env.PUBLIC_URL + "/images/flags/" + team.nationCode + ".png"}
                                alt={team.nationName}
                                className="w-7 h-7 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{team.nationName}</div>
                                <div className="text-[11px] text-slate-400 dark:text-slate-500">{totalParticipants} Athleten</div>
                              </div>
                              <div className="flex flex-col gap-0.5 flex-shrink-0">
                                <button
                                  onClick={() => moveTeamUp(idx)}
                                  disabled={isFirst}
                                  className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-up text-[9px]"></i>
                                </button>
                                <button
                                  onClick={() => moveTeamDown(idx)}
                                  disabled={isLast}
                                  className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                                >
                                  <i className="fas fa-chevron-down text-[9px]"></i>
                                </button>
                              </div>
                            </>
                          ) : (
                          <>
                          {/* Nationen-Header (ausklappbar) – View-Mode */}
                          <button
                            onClick={() => handleToggleNationExpansion(team.nationId)}
                            className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                <i className="fas fa-chevron-right text-slate-400 dark:text-slate-500 text-xs"></i>
                              </div>
                              <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                {startNum || (idx + 1)}
                              </span>
                              <img
                                src={process.env.PUBLIC_URL + "/images/flags/" + team.nationCode + ".png"}
                                alt={team.nationName}
                                className="w-7 h-7 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{team.nationName}</div>
                                <div className="text-[11px] text-slate-400 dark:text-slate-500">{totalParticipants} Athleten</div>
                              </div>
                            </div>
                          </button>

                          {/* Ausklappbare Teilnehmer-Cards */}
                          {isExpanded && (
                            <div className="border-t border-slate-100 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-700/20 px-4 py-3 space-y-1.5">
                              {team.participants.map((participant, pIdx) => (
                                <div key={participant.id || pIdx} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700/50">
                                  <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 text-[10px] font-bold text-slate-500 dark:text-slate-400">
                                    {pIdx + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{participant.name}</div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                                        <i className="fas fa-id-badge text-[8px]"></i>
                                        <span className="tabular-nums">{participant.participantNumber || participant.id}</span>
                                      </span>
                                      <span className="text-[10px] text-slate-400 dark:text-slate-500">{participant.age} J.</span>
                                    </div>
                                  </div>
                                  <span className={`flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                                    participant.gender === 'männlich'
                                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700/40'
                                      : 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border-pink-200 dark:border-pink-700/40'
                                  }`}>
                                    <i className={`fas ${participant.gender === 'männlich' ? 'fa-mars' : 'fa-venus'} text-[8px]`}></i>
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : !selectedGroup?.isPartnerForm ? (
              /* Normale Gruppen-Startliste */
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                      <i className="fas fa-list-ol text-[9px]"></i>
                      Startliste
                      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                        {groupParticipants.length}
                      </span>
                    </span>
                    <div className="flex items-center gap-2">
                      {isStartListEditMode ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-[11px] font-semibold">
                          <i className="fas fa-pencil-alt text-[9px]"></i>
                          Bearbeitung aktiv
                        </span>
                      ) : isStartListEditable ? (
                        <button
                          onClick={handleToggleStartListEditMode}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/30 dark:hover:border-primary-light/30 hover:text-primary dark:hover:text-primary-light transition-colors"
                        >
                          <i className="fas fa-sort text-[10px]"></i>
                          Reihenfolge
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500" title={`Bearbeitung nur bei Status "Ausstehend" möglich`}>
                          <i className="fas fa-lock text-[9px]"></i>
                          {selectedGroup?.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Edit mode banner */}
                {isStartListEditMode && (
                  <div className="mx-4 mt-4 px-3 py-2 bg-primary/5 dark:bg-primary-light/10 border border-primary/20 dark:border-primary-light/20 rounded-xl flex items-center gap-2">
                    <i className="fas fa-info-circle text-primary dark:text-primary-light text-xs flex-shrink-0"></i>
                    <span className="text-xs text-primary dark:text-primary-light flex-1">
                      Drag & Drop oder Pfeiltasten · Reihenfolge bestimmt Startnummer
                    </span>
                    <button
                      onClick={handleShuffle}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-primary/20 dark:border-primary-light/20 bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light text-xs font-medium hover:bg-primary/20 dark:hover:bg-primary-light/20 transition-colors"
                      title="Reihenfolge zufällig mischen"
                    >
                      <i className="fas fa-random text-[10px]"></i>
                      Shuffle
                    </button>
                  </div>
                )}

                {/* Card rows */}
                <div className="p-4 space-y-2">
                  {(isStartListEditMode ? participantOrder : groupParticipants).map((participant, index) => {
                    const list = isStartListEditMode ? participantOrder : groupParticipants;
                    const isFirst = index === 0;
                    const isLast = index === list.length - 1;
                    const nationCode = getNationCode(participant);
                    const nationName = getNationName(participant);
                    const name = participant.firstName && participant.lastName
                      ? `${participant.firstName} ${participant.lastName}`
                      : (typeof participant.name === 'string' ? participant.name : 'Unbekannt');
                    const formName = participant.openFormName ||
                      (participant.categories?.find(c => (c.name || c) === (selectedGroup?.category?.name || selectedGroup?.category))?.formName);

                    return (
                      <div
                        key={participant.id || index}
                        draggable={isStartListEditMode}
                        onDragStart={isStartListEditMode ? (e) => handleDragStart(e, index) : undefined}
                        onDragOver={isStartListEditMode ? handleDragOver : undefined}
                        onDrop={isStartListEditMode ? (e) => handleDrop(e, index) : undefined}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                          isStartListEditMode
                            ? 'cursor-grab bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 hover:border-primary/30 dark:hover:border-primary-light/30 hover:shadow-sm'
                            : 'bg-slate-50/50 dark:bg-slate-700/20 border-transparent'
                        }`}
                      >
                        {isStartListEditMode && (
                          <i className="fas fa-grip-vertical text-slate-300 dark:text-slate-600 flex-shrink-0"></i>
                        )}
                        <span className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          isStartListEditMode
                            ? 'bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                        }`}>
                          {index + 1}
                        </span>
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                          alt={nationName}
                          className="w-7 h-7 object-cover rounded-full border border-slate-200 dark:border-slate-600 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{name}</div>
                          <div className="flex items-center gap-2.5 mt-0.5 flex-wrap">
                            <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                              <i className="fas fa-id-badge text-[9px]"></i>
                              <span className="tabular-nums font-medium">{participant.participantNumber || participant.id}</span>
                            </span>
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{nationName}</span>
                            <span className="text-[11px] text-slate-400 dark:text-slate-500">{participant.age} J.</span>
                            {selectedGroup?.category?.type === 'Offene Form' && formName && (
                              <span className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500 italic">
                                <i className="fas fa-scroll text-[9px]"></i>
                                {formName}
                              </span>
                            )}
                          </div>
                        </div>
                        {isStartListEditMode && (
                          <div className="flex flex-col gap-0.5 flex-shrink-0">
                            <button
                              onClick={() => moveParticipantUp(index)}
                              disabled={isFirst}
                              className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-chevron-up text-[9px]"></i>
                            </button>
                            <button
                              onClick={() => moveParticipantDown(index)}
                              disabled={isLast}
                              className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-primary dark:hover:text-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                            >
                              <i className="fas fa-chevron-down text-[9px]"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </AdminModalTailwind>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />
    </>
  );
};

export default GroupsTailwind;
